import { t } from '../i18n';
import { fileTypeImg } from './fileTypeImg';

/** How many trailing path segments to keep when collapsing long breadcrumbs. */
export const BREADCRUMB_MAX_TAIL = 2;

export interface BreadcrumbSegment {
  label: string;
  current: boolean;
  onNavigate: () => void;
}

export function shouldCollapseBreadcrumb(
  segmentCount: number,
  maxTail = BREADCRUMB_MAX_TAIL,
): boolean {
  return segmentCount > maxTail;
}

function appendSep(host: HTMLElement): void {
  const sep = document.createElement('span');
  sep.className = 'files-folder-breadcrumb__sep';
  sep.setAttribute('aria-hidden', 'true');
  sep.textContent = '›';
  host.append(sep);
}

/**
 * Shared breadcrumb renderer with mobile-friendly collapse:
 * `/ › … › penultimate › current` when there are more than {@link BREADCRUMB_MAX_TAIL} segments.
 * The ellipsis navigates to the last hidden segment (parent of the first visible).
 */
export function renderBreadcrumbNav(
  host: HTMLElement,
  options: {
    onRoot: () => void;
    rootCurrent: boolean;
    segments: BreadcrumbSegment[];
    maxTail?: number;
  },
): void {
  const maxTail = options.maxTail ?? BREADCRUMB_MAX_TAIL;
  host.replaceChildren();
  host.classList.add('files-folder-breadcrumb');
  host.setAttribute('aria-label', '/');

  const rootButton = document.createElement('button');
  rootButton.type = 'button';
  rootButton.className = `files-folder-breadcrumb__crumb${options.rootCurrent ? ' files-folder-breadcrumb__crumb--current' : ''}`;
  rootButton.append(fileTypeImg('folder', 14), document.createTextNode('/'));
  rootButton.addEventListener('click', options.onRoot);
  host.append(rootButton);

  const segments = options.segments;
  const collapsed = shouldCollapseBreadcrumb(segments.length, maxTail);
  const startIndex = collapsed ? Math.max(0, segments.length - maxTail) : 0;

  if (collapsed && startIndex > 0) {
    appendSep(host);
    const ellipsis = document.createElement('button');
    ellipsis.type = 'button';
    ellipsis.className = 'files-folder-breadcrumb__crumb files-folder-breadcrumb__crumb--ellipsis';
    ellipsis.textContent = '…';
    ellipsis.setAttribute('aria-label', t('files.browse.breadcrumbMore'));
    ellipsis.title = t('files.browse.breadcrumbMore');
    const hiddenParent = segments[startIndex - 1];
    ellipsis.addEventListener('click', () => hiddenParent?.onNavigate());
    host.append(ellipsis);
  }

  for (let i = startIndex; i < segments.length; i += 1) {
    const segment = segments[i];
    if (!segment) continue;
    appendSep(host);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `files-folder-breadcrumb__crumb${segment.current ? ' files-folder-breadcrumb__crumb--current' : ''}`;
    button.textContent = segment.label;
    button.addEventListener('click', segment.onNavigate);
    host.append(button);
  }
}

export function renderFilesBreadcrumb(
  host: HTMLElement,
  currentPath: string,
  onNavigate: (path: string) => void,
  options?: { rootPath?: string },
): void {
  const rootPath = (options?.rootPath ?? '').replace(/^\/+|\/+$/g, '');
  const relativePath = rootPath
    ? (currentPath === rootPath
      ? ''
      : currentPath.startsWith(`${rootPath}/`)
        ? currentPath.slice(rootPath.length + 1)
        : '')
    : currentPath;

  const parts = relativePath ? relativePath.split('/') : [];
  const segments: BreadcrumbSegment[] = [];
  let builtRelative = '';
  for (const part of parts) {
    builtRelative = builtRelative ? `${builtRelative}/${part}` : part;
    const absolutePath = rootPath
      ? (builtRelative ? `${rootPath}/${builtRelative}` : rootPath)
      : builtRelative;
    segments.push({
      label: part,
      current: absolutePath === currentPath,
      onNavigate: () => onNavigate(absolutePath),
    });
  }

  renderBreadcrumbNav(host, {
    onRoot: () => onNavigate(rootPath),
    rootCurrent: relativePath === '',
    segments,
  });
}
